/*
 * Pages component for paginated forms.
 */

import React from 'react';
import { DndProvider } from 'react-dnd';
import Backend from 'react-dnd-html5-backend';

import Base from './base.jsx'; 
import { Mode, ValidationType } from '../common.jsx';
import Helpers from './helpers.jsx'
import Resolver from '../component_resolver.jsx';
import { resolveComponentProps } from '../form_viewer.jsx';
import { DragHandle, DropArea } from '../../common/DragNDrop.jsx';
import { FaIcon } from '../../common/Icons.jsx';

const DND_ITEM_TYPE = 'page-selection-option';

export default class Pages extends Base {

        static get CATEGORY() {
            return 'layout';
        }

        static get TYPE() {
            return 'pages';
        }

        static icon(){
            return <i className='fa fa-th'></i>;
        }

        static defaultContent(language) {
            return Object.assign(super.defaultContent(language), {
                cloneable: false,
                deletable: false,
                sortable: false,
                pagesData: [
                    {id: Math.random().toString(36).slice(2), name: ''}
                ],
                rows: [ // Pages
                    [ // Components within page
                    ]
                ],
            });
        }
        
        static nestedComponents(parentComponentContent) {
            let components = [];
            const pageIds = (parentComponentContent.pagesData || []).map(({id}) => id);
            parentComponentContent.rows.forEach((page, pageIdx) => {
                page.forEach((section) => {
                    if (section.component && Resolver.componentClass(section.component.type)) {
                        section.component.pageId = pageIds[pageIdx];
                        components.push(section.component);
                        const nested = Resolver.componentClass(section.component.type).nestedComponents(section.component);
                        nested.forEach(component => component.pageId = pageIds[pageIdx]);
                        components = components.concat(nested);
                    }
                });
            });
            return components;
        }


        static deleteNestedComponent(componentContent, componentId) {
            let found = false;

            componentContent.rows.forEach((page) => {
                page.forEach((section, index) => {
                    if (found) {
                        return;
                    }
                    if (section.component && section.component.id == componentId) {
                        page.splice(index, 1);
                        found = true;
                    } else if (section.component &&Resolver.componentClass(section.component.type).deleteNestedComponent(section.component, componentId)) {
                        found = true;
                    }
                });
            });
            return found;
        }

        static dropComponent(dropArea, targetContent, droppedContent, components, isNested) {
            let dropped = false;

            if (targetContent.id == dropArea.getAttribute('data-component-id') && Resolver.componentClass(droppedContent.type).dropAllowed(dropArea, droppedContent, components, this.isNested() || isNested)) {
                let context = dropArea.getAttribute('data-context').split('-');
                let rowIdx = parseInt(context[0], 10);
                let componentIdx = parseInt(context[1], 10);

                targetContent.rows[rowIdx] = targetContent.rows[rowIdx] || [];
                targetContent.rows[rowIdx][componentIdx] = targetContent.rows[rowIdx][componentIdx] || {component: null};
                targetContent.rows[rowIdx][componentIdx].component = JSON.parse(JSON.stringify(droppedContent));

                dropped = true;
            } else {
                this.nestedComponents(targetContent).forEach((c) => {
                    if (Resolver.componentClass(c.type).dropComponent(dropArea, c, droppedContent, components, this.isNested() || isNested)) {
                        dropped = true;
                        return;
                    }
                });
            }

            return dropped;
        }

        constructor(props) {
            super(props);
            this.addRow = this.addRow.bind(this);
            this.deleteRow = this.deleteRow.bind(this);
            this.reorderOption = this.reorderOption.bind(this);
            this.setPage = this.setPage.bind(this);
            this.toggleNavigator = this.toggleNavigator.bind(this);
            this.state = {
                order: [...props?.content?.pagesData?.map(({id}) => id) || []],
                showNavigator: false
            } 
        }

        componentProps() {
            return {};
        }

        componentDidMount() {
            super.componentDidMount();
            this.syncPaginationPageCount();
        }

        componentDidUpdate() {
            super.componentDidUpdate();
            this.syncPaginationPageCount();
        }

        syncPaginationPageCount() {
            const { pagination, content } = this.props;
            const pageCount = content?.rows?.length ?? 0;

            if (pagination && pagination.pageCount !== pageCount) {
                pagination.setPageCount(pageCount);
            }
        }

        setPage(page) {
            if (this.props.pagination) {
                this.props.pagination.setShownPageIndex(page);
            };
        }

        toggleNavigator() {
            this.setState(prev => ({ showNavigator: !prev.showNavigator }));
        }

        renderView() {
            const shownPageIndex = this.props.pagination?.shownPageIndex || 0;
            const paged = [Mode.VIEW, Mode.FILL].includes(this.props.mode) && !this.props.pdf && !this.props.disablePagedMode;
            const pagesData = this.props.content.pagesData || [];
            const visitedPages = this.props.pagination?.visitedPages || [shownPageIndex];

            const {content, asWebView} = this.props
            let pages = this.props.content.rows.map((page, pageIdx) => {
                let sections = page.map((section, componentIdx) => {
                    let component = null;

                    if (section.component) {
                        section.component.repeatedFieldScope = this.props.repeatedFieldScope;
                        component = Resolver.createElement(section.component.type, 
                          resolveComponentProps(section.component, this.props, {
                            swapComponentsOnDrop: true, 
                            selectCallback: this.props.selectCallback, 
                            selectedId: this.props.selectedId,
                            deleteRepeatableRegionAnswerCallback: this.props.deleteRepeatableRegionAnswerCallback,
                            fieldScope: this.props.fieldScope,
                            repeatedFieldScope: this.props.repeatedFieldScope,
                            ...this.componentProps(section.component),
                          })
                        );
                    }

                    const styles = {
                        flex: 1,
                        flexBasis: asWebView ? 'min-content' : 0,
                        overflowX: asWebView ? 'auto' : 'visible'
                    }
                    return (<>
                        {component &&
                            <div key={componentIdx} className='page-component grid-row' style={styles} data-flex={1}>
                                {component}
                            </div>
                        }
                    </>);
                });

                const className = !paged || shownPageIndex == pageIdx ? '' : 'hidden-page';
                const dropAreaStyle = {
                    marginBottom: 0,
                    marginTop: page.length ? 10 : 0
                };
                return (<>
                    {pageIdx !== 0 && !paged &&
                    <>
                        {this.props.pdf
                            ? <div className='page-break-before' />
                            : this.props.mode === Mode.EDIT &&
                                <div className="page-break-container">
                                    <div className='page-break-content'>
                                        <small>
                                            {I18n.t('end_of_page_name', {scope: 'fe', name: pagesData[pageIdx - 1]?.name ? `"${pagesData[pageIdx - 1]?.name}"` : pageIdx})}
                                        </small>
                                        <span>{I18n.t('page_break', {scope: 'fe'})}</span>
                                    </div>
                                </div>
                        }
                    </>}

                    <div key={pageIdx} className={`page ${className}`}>
                        <div>
                            {!paged && (this.props.pdf || this.props.mode === Mode.EDIT || this.props.disablePagedMode) &&
                                <h2>{pagesData[pageIdx]?.name ? pagesData[pageIdx]?.name : I18n.t('page_number', {scope: 'fe', number: pageIdx + 1})}</h2>
                            }
                            {sections}
                            {this.inEditMode &&
                                <Helpers.DropArea
                                    style={dropAreaStyle}
                                    componentId={this.props.content.id}
                                    context={pageIdx + '-' + page.length}
                                    textContent={I18n.t('drag_and_drop_a_component', {scope: 'fe'})}
                                />
                            }
                        </div>
                    </div>
                </>);
            });

            const totalPages = pages.length;
            const invalidPageValidationFailures = this.getPageValidationFailures();
            const progress = this.getRequiredFieldProgressByPage();

            return (<>
                <div className={`pages-container ${this.state.showNavigator ? 'show-navigator' : ''}`}>
                    {paged && (
                        <div className='mobile-navigator'>
                            <div className='page-count'>{I18n.t('page_number_of_total', {number: shownPageIndex + 1, total: totalPages})}</div>
                            <div className='mobile-page-items'>
                                {pagesData.map(
                                    ({ name, id }, idx) => {
                                        const invalidErrors = Object.values(invalidPageValidationFailures?.[id] || {}).flat();
                                        const shown = shownPageIndex === idx;
                                        const errorCount = invalidErrors.length;
                                        return (
                                            <div className={`mobile-page-step ${shownPageIndex >= idx ? 'current-or-before' : ''}`} key={id}>
                                                {idx > 0 && <div className='mobile-page-line' />}
                                                <div
                                                    className={`mobile-page-item ${shown ? 'shown' : ''} ${errorCount > 0 && !shown ? 'has-errors' : ''}`}
                                                    onClick={() => this.setPage(idx)}
                                                />
                                            </div>
                                        )
                                    }
                                )}
                            </div>
                            <div className='chevron-container' onClick={this.toggleNavigator}>
                                <FaIcon type={this.state.showNavigator ? 'chevron-up' : 'chevron-down'} />
                            </div>
                        </div>
                    )}
                    {paged && (
                        <div className='page-navigator thin-scrollbar'>
                            {pagesData.map(({ name, id }, idx) => {
                            const invalidErrors = Object.values(invalidPageValidationFailures?.[id] || {}).flat();
                            const { total = 0, answered = 0 } = progress?.[id] || {};
                            const hasMissingRequired = answered < total;

                            const shown = shownPageIndex === idx;
                            const errorCount = invalidErrors.length;
                            const hasErrors = errorCount > 0;
                            const valid = !shown && !hasMissingRequired && !hasErrors && visitedPages.includes(idx) && this.props.mode === Mode.FILL;

                            let progressPercentage = total === 0 ? 0 : 100 * (answered / total);
                            progressPercentage = progressPercentage === 100 ? 101 : progressPercentage;

                                return (
                                    <div
                                        key={id}
                                        data-page-id={id}
                                        className={`page-item ${shown ? 'shown' : ''} ${valid ? 'valid' : ''} ${hasErrors && !shown ? 'has-errors' : ''}`}
                                        onClick={() => this.setPage(idx)}
                                        style={{'--progress': `${progressPercentage}%`}}
                                    >
                                        <div className='page-item-inner'>
                                            <div className='page-info'>
                                                <div>
                                                    <b>{name}</b>
                                                </div>
                                            </div>
                                            <div className='page-item-notification'>
                                            <span className='text-truncate'>
                                                {hasErrors
                                                    ? I18n.t(errorCount > 1 ? 'validation_errors_count' : 'validation_one_error', {count: errorCount})
                                                    : total > 0
                                                        ? I18n.t('answered_of_total_required', {answered, total})
                                                        : I18n.t('zero_required')
                                                }
                                            </span>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    )}
                    <div className='pages-wrapper'>
                        {pages}
                    </div>
                </div>
            </>);
        }

        renderSettings() {
            let spanOptions = [];
            for (let i = 0; i < 12; i++) {
                spanOptions.push((<option key={i} value={i + 1}>{i + 1}</option>));
            }
            const pageNames = this.props.content.pagesData.map(({name}) => name) || []
            let rows = this.props.content.rows.map((r, ri) => {
                let header = [];
                header.push(<td key={header.length}></td>);
                let columns = [];
                columns.push(
                    <td key={`pr-${ri}`} className='page-row'>
                        <input
                            type='text'
                            name={'pageNames|' + ri}
                            defaultValue={pageNames[ri] || ''}
                            onChange={this.updateSettings}
                            className=''
                        />
                    </td>
                );
                columns.push(
                    <td key={`i-${ri}`} className=''>
                        {(!this.props.safeMode || r.every(v => !v.component || v.component?.isNewComponent)) &&
                            <a href='#' className='icon-trash' title={I18n.t('delete_row', {scope: 'fe'})} data-row-index={ri} onClick={this.deleteRow}></a>
                        }
                    </td>
                );
                const currentRowId = this.state?.order ? this.state.order[ri] : 0;
                return (
                    <DropArea
                        key={`ri-${currentRowId}`}
                        accept={DND_ITEM_TYPE}
                        item={{ id: `ri-${currentRowId}`, index: ri, type: DND_ITEM_TYPE }}
                        onMove={this.reorderOption}
                        render={({ dropRef, dragRef, isDragging }) => (
                            <div key={`ri-${currentRowId}`} className='grid-row' ref={dropRef} style={{display:'flex'}}>
                                <DragHandle dragRef={dragRef} />
                                <table>
                                    <tbody>
                                        <tr>{header}</tr>
                                        <tr className='columns page-settings-row'>{columns}</tr>
                                    </tbody>
                                </table>
                            </div>
                        )}
                    />
                );
            });
            return (
                <div ref='settings'>
                    <div className='setting'>
                        <label>
                            {I18n.t('pages', {scope: 'fe'})}
                        </label>
                        <DndProvider backend={Backend} context={window} key={1}>
                            <div className='grid-rows'>
                                {rows}
                            </div>
                        </DndProvider>
                    <a href='#' className='btn btn-primary' onClick={this.addRow}>
                        <i className='icon-add'></i> {I18n.t('add_page', {scope: 'fe'})}
                    </a>
                    </div>
                </div>
            );
        }

        reorderOption(dragIndex, hoverIndex) {
            const rowsCopy = JSON.parse(JSON.stringify(this.props.content.rows));
            const pagesDataCopy = JSON.parse(JSON.stringify(this.props.content.pagesData));
            const currentOrder = [...this.state.order];

            const [draggedRow] = rowsCopy.splice(dragIndex, 1);
            const [draggedPageData] = pagesDataCopy.splice(dragIndex, 1);
            const [removedOrder] = currentOrder.splice(dragIndex, 1);

            rowsCopy.splice(hoverIndex, 0, draggedRow);
            pagesDataCopy.splice(hoverIndex, 0, draggedPageData);
            currentOrder.splice(hoverIndex, 0, removedOrder);

            this.setState({order: currentOrder});

            const updatedContent = {
                id: this.props.content.id,
                rows: rowsCopy,
                pagesData: pagesDataCopy
            };

            this.props.updateSettingsCallback(updatedContent.id, updatedContent, true);
        }

        addRow(event) {
              let content = {id: this.props.content.id};

              content.rows = JSON.parse(JSON.stringify(this.props.content.rows));
              content.pagesData = JSON.parse(JSON.stringify(this.props.content.pagesData));

              const newId = Math.random().toString(36).slice(2);
              content.rows.push([]);
              content.pagesData.push({id: Math.random().toString(36).slice(2), name: ''});

              let addedOrder = this.state.order;
              addedOrder.push(newId);
              this.setState({order: addedOrder});

              this.props.updateSettingsCallback(content.id, content, true);

              event?.preventDefault();
        }

        deleteRow(event) {
            if (this.props.content.rows.length < 2) {
                alert(I18n.t('alert_page_deletion', {scope: 'fe'}));
            } else if (confirm(I18n.t('confirm_delete_page', {scope: 'fe'}))) {
                const rowIdx = parseInt(event.currentTarget.getAttribute('data-row-index'), 10);

                const rows = [...this.props.content.rows];
                const pagesData = [...this.props.content.pagesData];
                const order = [...this.state.order];

                rows.splice(rowIdx, 1);
                pagesData.splice(rowIdx, 1);
                order.splice(rowIdx, 1);

                this.setState({order});

                const content = {...this.props.content, rows, pagesData};

                this.beforeUpdateSettings(content, rowIdx);
                this.props.updateSettingsCallback(content.id, content, true);
            }

            event.preventDefault();
        }

        beforeUpdateSettings(content, deletedRowIdx) {
            content.rows = JSON.parse(JSON.stringify(this.props.content.rows));
            content.pagesData = JSON.parse(JSON.stringify(this.props.content.pagesData || []));

            if (deletedRowIdx !== undefined && deletedRowIdx !== null) {
                content.rows.splice(deletedRowIdx, 1);
                content.pagesData.splice(deletedRowIdx, 1);
            }

            Object.keys(content)
                .filter(k => k.match(/^pageNames\|/))
                .forEach(k => {
                    const pageIdx = parseInt(k.split('|')[1], 10);
                    content.pagesData[pageIdx] = {
                        ...content.pagesData[pageIdx],
                        name: content[k] || ''
                    };
                    delete content[k];
                });
        }

        renderAdvancedSettings() {
            return null;
        }
}
